import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AthleteContext } from "./App";
import FilterTags from "./FilterTags";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Sectie-definitie ──────────────────────────────────────
const TERMS_URL = "/content/deelnemersvoorwaarden";

const SECTIONS = [
  {
    key: "personal",
    title: "Persoonlijke gegevens",
    info: "Kies een weergavenaam en schrijf een korte tekst. Dit is zichtbaar op je profielpagina.",
    wizardTitle: "Wie ben jij?",
    wizardDesc: "Kies hoe je wilt worden weergegeven en vertel iets over jezelf."
  },
  {
    key: "participation",
    title: "Deelname aan het event",
    info: "Geef aan of je meedoet aan de grote routerit, een deel ervan, of alleen individuele activiteiten.",
    wizardTitle: "Doe je mee aan het event?",
    wizardDesc: "Kies je vorm van deelname en selecteer eventueel de etappes."
  },
  {
    key: "contact",
    title: "Contact",
    info: "Je e-mailadres is verplicht bij deelname aan een route. We gebruiken het alleen voor praktische informatie en updates.",
    wizardTitle: "Hoe kunnen we je bereiken?",
    wizardDesc: "Laat je e-mailadres achter voor informatie over de etappes."
  },
  {
    key: "target",
    title: "Streefbedrag",
    info: "Stel in hoeveel je via donaties wilt ophalen. Verschijnt als voortgangsbalk op je profielpagina.",
    wizardTitle: "Wat is je streefbedrag?",
    wizardDesc: "Optioneel: geef aan hoeveel donaties je wilt ophalen."
  },
  {
    key: "filters",
    title: "Activiteitsfilters",
    info: "Strava-activiteiten die deze trefwoorden bevatten worden gesynchroniseerd naar de kaart.",
    wizardTitle: "Welke activiteiten tellen mee?",
    wizardDesc: "Strava-activiteiten met deze trefwoorden worden zichtbaar op de kaart."
  },
  {
    key: "terms",
    title: "Voorwaarden",
    info: "Lees de deelnemersvoorwaarden voor je je aanmelding afrondt.",
    wizardTitle: "Bijna klaar!",
    wizardDesc: "Lees en accepteer de deelnemersvoorwaarden om je aanmelding te voltooien."
  }
];

// ── Herbruikbare info-popover ─────────────────────────────
function InfoButton({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="ob-info-wrapper">
      <button
        type="button"
        className="ob-info-btn"
        onClick={e => { e.stopPropagation(); setShow(s => !s); }}
        aria-label="Meer informatie"
      >ⓘ</button>
      {show && (
        <div className="ob-info-popup" onClick={() => setShow(false)}>
          {text}
        </div>
      )}
    </span>
  );
}

// ── Sectie-card voor profiel-modus ────────────────────────
function SectionCard({ section, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="ob-section-card">
      <div className="ob-section-header" onClick={() => setOpen(o => !o)}>
        <span className="ob-section-title">{section.title}</span>
        <span className="ob-section-right">
          <InfoButton text={section.info} />
          <span className="ob-section-arrow">{open ? "▲" : "▼"}</span>
        </span>
      </div>
      {open && <div className="ob-section-body">{children}</div>}
    </div>
  );
}

// ── Hoofd-component ───────────────────────────────────────
export default function Onboarding() {
  const { currentAthlete, setCurrentAthlete, sessionChecked } = useContext(AthleteContext);
  const navigate = useNavigate();

  const [csrfToken, setCsrfToken] = useState(null);
  const [availableDays, setAvailableDays] = useState([]);
  const [saved, setSaved] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // Form state
  const [nickname, setNickname] = useState("");
  const [bio, setBio] = useState("");
  const [email, setEmail] = useState("");
  const [notifyByEmail, setNotifyByEmail] = useState(false);
  const [targetAmount, setTargetAmount] = useState(0);
  const [participationType, setParticipationType] = useState("none");
  const [participationDays, setParticipationDays] = useState([]);
  const [overnightDays, setOvernightDays] = useState([]);
  const [eventFilters, setEventFilters] = useState([]);
  const [trainingFilters, setTrainingFilters] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/csrf-token`, { credentials: "include" })
      .then(r => r.json())
      .then(data => setCsrfToken(data.csrfToken));
    fetch(`${API}/event-routes`)
      .then(r => r.json())
      .then(data => setAvailableDays(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!currentAthlete) return;
    setNickname(currentAthlete.nickname || "");
    setBio(currentAthlete.bio || "");
    setEmail(currentAthlete.email || "");
    setNotifyByEmail(currentAthlete.notifyByEmail || false);
    setTargetAmount(currentAthlete.targetAmount || 0);
    setParticipationType(currentAthlete.participationType || "none");
    setParticipationDays(currentAthlete.participationDays || []);
    setOvernightDays(currentAthlete.overnightDays || []);
    setEventFilters(currentAthlete.eventFilters || []);
    setTrainingFilters(currentAthlete.trainingFilters || []);
    setTermsAccepted(currentAthlete.termsAccepted || false);
  }, [currentAthlete]);

  // ── Sessie-check ──────────────────────────────────────
  useEffect(() => {
    const check = () => {
      fetch(`${API}/athlete/session`, { credentials: "include" })
        .then(r => r.json())
        .then(data => { if (!data.authenticated) navigate("/aanmelden"); });
    };
    const interval = setInterval(check, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [navigate]);

  // ── Helpers ───────────────────────────────────────────
  const allOvernightDays = days => days.slice(0, -1).map(d => d.dayNumber);
  const routeParticipant = participationType === "full" || participationType === "partial";

  const handleTypeChange = type => {
    setParticipationType(type);
    if (type === "full" && availableDays.length > 0) setOvernightDays(allOvernightDays(availableDays));
  };

  const toggleDay = dayNumber => {
    setParticipationDays(prev =>
      prev.includes(dayNumber) ? prev.filter(d => d !== dayNumber) : [...prev, dayNumber].sort((a, b) => a - b)
    );
  };

  const toggleOvernight = dayNumber => {
    setOvernightDays(prev =>
      prev.includes(dayNumber) ? prev.filter(d => d !== dayNumber) : [...prev, dayNumber].sort((a, b) => a - b)
    );
  };

  const validate = () => {
    if (routeParticipant && !email.trim()) {
      alert("Vul je e-mailadres in — verplicht bij deelname aan een route.");
      return false;
    }
    if (!termsAccepted) {
      alert("Accepteer de deelnemersvoorwaarden om door te gaan.");
      return false;
    }
    return true;
  };

  const save = () => {
    if (!validate()) return;
    fetch(`${API}/athlete/onboarding`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "CSRF-Token": csrfToken },
      body: JSON.stringify({
        nickname, bio, email, notifyByEmail, targetAmount, termsAccepted,
        eventFilters, trainingFilters,
        participationType, participationDays,
        overnightDays: participationType === "partial"
          ? overnightDays.filter(d => participationDays.includes(d) && participationDays.includes(d + 1))
          : overnightDays
      })
    })
      .then(r => { if (r.status === 401) { navigate("/aanmelden"); return null; } return r.json(); })
      .then(data => {
        if (!data) return;
        setCurrentAthlete(a => ({ ...a, nickname, bio, email, notifyByEmail, targetAmount, termsAccepted, eventFilters, trainingFilters, participationType, participationDays, overnightDays, active: true }));
        setSaved(true);
        setTimeout(() => setSaved(false), 4000);
      });
  };

  const logout = () => {
    fetch(`${API}/athlete/logout`, { method: "POST", credentials: "include", headers: { "CSRF-Token": csrfToken } })
      .then(() => { setCurrentAthlete(null); navigate("/"); });
  };

  const [stepError, setStepError] = useState("");

  const validateStep = (step) => {
    const key = SECTIONS[step - 1]?.key;
    if (key === "contact" && routeParticipant && !email.trim()) {
      return "Vul je e-mailadres in — verplicht bij deelname aan een route.";
    }
    return "";
  };

  const nextStep = () => {
    const err = validateStep(wizardStep);
    if (err) { setStepError(err); return; }
    setStepError("");
    setWizardStep(s => Math.min(s + 1, SECTIONS.length));
  };
  const prevStep = () => { setStepError(""); setWizardStep(s => Math.max(s - 1, 1)); };

  // ── Wachten op sessie ─────────────────────────────────
  if (!sessionChecked) return <p>Laden…</p>;
  if (!currentAthlete) {
    return (
      <div className="pane pane-center dark_pane">
        <h1>Niet ingelogd</h1>
        <p>Je bent niet aangemeld. <a href="/aanmelden">Meld je aan via Strava</a> om je profiel in te stellen.</p>
      </div>
    );
  }

  const isFirstTime = !currentAthlete.active;
  const currentSection = SECTIONS[wizardStep - 1];

  // ── Inhoud per sectie ─────────────────────────────────
  const sectionPersonal = (
    <>
      <label htmlFor="nickname">Weergavenaam (optioneel)</label>
      <input id="nickname" value={nickname} onChange={e => setNickname(e.target.value)} placeholder="Naam of nickname" />
      <label htmlFor="bio">Persoonlijke tekst (optioneel)</label>
      <small>Zichtbaar op je profielpagina. Alleen vetgedrukt en cursief zijn toegestaan.</small>
      <textarea
        id="bio" value={bio} onChange={e => setBio(e.target.value)}
        placeholder="Vertel iets over jezelf…" rows={4}
        style={{ width: "100%", boxSizing: "border-box", marginBottom: "0.5rem" }}
      />
    </>
  );

  const sectionParticipation = (
    <div className="onboarding-participation">
      <div className={`ob-option${participationType === "full" ? " selected" : ""}`} onClick={() => handleTypeChange("full")}>
        <div className="ob-option-header">
          <span className="ob-circle">{participationType === "full" ? "✓" : ""}</span>
          <span>Ik doe mee aan de hele grote actie</span>
        </div>
        {participationType === "full" && availableDays.length > 0 && (
          <div className="ob-sub" onClick={e => e.stopPropagation()}>
            <span className="ob-sub-label">Na welke etappes wil je overnachten?</span>
            {availableDays.slice(0, -1).map(day => {
              const on = overnightDays.includes(day.dayNumber);
              return (
                <div key={day.dayNumber} className={`ob-overnight-full${on ? " selected" : ""}`} onClick={() => toggleOvernight(day.dayNumber)}>
                  <span className={`ob-square${on ? " selected" : ""}`}>{on ? "✓" : ""}</span>
                  <span>Na dag {day.dayNumber} — {day.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className={`ob-option${participationType === "partial" ? " selected" : ""}`} onClick={() => handleTypeChange("partial")}>
        <div className="ob-option-header">
          <span className="ob-circle">{participationType === "partial" ? "✓" : ""}</span>
          <span>Ik doe mee aan een deel van de grote actie</span>
        </div>
        {participationType === "partial" && availableDays.length > 0 && (
          <div className="ob-sub" onClick={e => e.stopPropagation()}>
            {availableDays.map((day, idx) => {
              const checked = participationDays.includes(day.dayNumber);
              const isLast = idx === availableDays.length - 1;
              const nextDay = availableDays[idx + 1];
              const canOvernight = checked && !isLast && nextDay && participationDays.includes(nextDay.dayNumber);
              const isOvernighting = overnightDays.includes(day.dayNumber);
              return (
                <div key={day.dayNumber} className={`ob-day${checked ? " selected" : ""}`} onClick={() => toggleDay(day.dayNumber)}>
                  <div className="ob-day-header">
                    <span className={`ob-square${checked ? " selected" : ""}`}>{checked ? "✓" : ""}</span>
                    <span><strong>Dag {day.dayNumber}</strong> — {day.name}{day.distanceKm > 0 && <small> ({day.distanceKm} km)</small>}</span>
                  </div>
                  {checked && !isLast && (
                    <div
                      className={`ob-overnight${canOvernight && isOvernighting ? " selected" : ""}${!canOvernight ? " disabled" : ""}`}
                      onClick={e => { e.stopPropagation(); if (canOvernight) toggleOvernight(day.dayNumber); }}
                    >
                      <span className={`ob-square${canOvernight && isOvernighting ? " selected" : ""}${!canOvernight ? " disabled" : ""}`}>
                        {canOvernight && isOvernighting ? "✓" : ""}
                      </span>
                      <span>Overnachten na dag {day.dayNumber}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {[
        { value: "individual", label: "Ik doe zelf individuele acties (lopen, fietsen, etc.)" },
        { value: "none", label: "Nog niet besloten" }
      ].map(opt => (
        <div key={opt.value} className={`ob-option${participationType === opt.value ? " selected" : ""}`} onClick={() => handleTypeChange(opt.value)}>
          <div className="ob-option-header">
            <span className="ob-circle">{participationType === opt.value ? "✓" : ""}</span>
            <span>{opt.label}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const sectionContact = (
    <>
      <label htmlFor="email">
        E-mailadres
        {routeParticipant && <span style={{ color: "#c00", marginLeft: 4 }}>*</span>}
      </label>
      <small>{routeParticipant ? "Verplicht bij deelname aan een route — voor praktische informatie." : "Optioneel — voor updates over de actie."}</small>
      <input
        id="email" type="email" value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="jouw@emailadres.nl"
      />
      <label className="onboarding-check" style={{ marginTop: "0.5rem" }}>
        <input type="checkbox" checked={notifyByEmail} onChange={e => setNotifyByEmail(e.target.checked)}
          style={{ display: "inline", width: "auto", margin: 0 }} />
        Ik wil op de hoogte worden gehouden
      </label>
    </>
  );

  const sectionTarget = (
    <>
      <label htmlFor="targetAmount">Streefbedrag (€)</label>
      <small>Hoeveel donaties wil je ophalen? Wordt getoond als voortgangsbalk op je profielpagina.</small>
      <div className="ap-amount-row">
        <span className="ap-eur">€</span>
        <input
          id="targetAmount" type="number" min="0" step="50"
          value={targetAmount || ""} onChange={e => setTargetAmount(Number(e.target.value) || 0)}
          placeholder="0" className="ap-amount-input"
        />
      </div>
    </>
  );

  const sectionFilters = (
    <>
      <label>Activiteitsfilter evenementen</label>
      <small>Zoektermen in naam of beschrijving van Strava-activiteiten.</small>
      <FilterTags filters={eventFilters} onChange={setEventFilters} placeholder="bijv. Hartekind" />
      <label>Activiteitsfilter trainingen</label>
      <small>Zelfde als boven, maar gemarkeerd als training op de kaart.</small>
      <FilterTags filters={trainingFilters} onChange={setTrainingFilters} placeholder="bijv. training" />
    </>
  );

  const sectionTerms = (
    <div>
      <p>
        Lees de{" "}
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">deelnemersvoorwaarden</a>
        {" "}voordat je je aanmelding voltooit.
      </p>
      <label className="onboarding-check" style={{ marginTop: "0.75rem" }}>
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={e => setTermsAccepted(e.target.checked)}
          style={{ display: "inline", width: "auto", margin: 0 }}
        />
        Ik heb de deelnemersvoorwaarden gelezen en ga hiermee akkoord
        <span style={{ color: "#c00", marginLeft: 4 }}>*</span>
      </label>
    </div>
  );

  const sectionContent = { personal: sectionPersonal, participation: sectionParticipation, contact: sectionContact, target: sectionTarget, filters: sectionFilters, terms: sectionTerms };

  // ── Render ────────────────────────────────────────────
  return (
    <div className="pane pane-center dark_pane">
      <div className="profile-header">
        {currentAthlete.profilePhoto && (
          <img src={currentAthlete.profilePhoto} alt={currentAthlete.firstName} className="profile-photo" />
        )}
        <h1>Mijn profiel</h1>
      </div>
      <p>Welkom, <strong>{currentAthlete.firstName} {currentAthlete.lastName}</strong>.</p>

      {!currentAthlete.approved && (
        <p className="pending-notice">
          Je aanmelding wacht nog op goedkeuring door de beheerder. Je routes worden daarna gesynchroniseerd.
        </p>
      )}

      {/* ── Wizard-modus (eerste keer) ── */}
      {isFirstTime ? (
        <div className="wizard">
          <div className="wizard-progress">
            {SECTIONS.map((s, i) => (
              <div key={s.key} className={`wizard-dot${wizardStep === i + 1 ? " active" : wizardStep > i + 1 ? " done" : ""}`} />
            ))}
          </div>
          <div className="wizard-step-header">
            <h2 className="wizard-step-title">{currentSection.wizardTitle}</h2>
            <p className="note">{currentSection.wizardDesc}</p>
          </div>
          <div className="wizard-step-body">
            {sectionContent[currentSection.key]}
          </div>
          {stepError && <p className="content-error" style={{ marginBottom: "0.5rem" }}>{stepError}</p>}
          <div className="wizard-nav">
            <div className="wizard-step-label">Stap {wizardStep} van {SECTIONS.length}</div>
            <div className="wizard-nav-actions">
              {wizardStep > 1 && (
                <button type="button" className="btn-secondary" onClick={prevStep}>← Vorige</button>
              )}
              {wizardStep < SECTIONS.length ? (
                <button type="button" onClick={nextStep}>Volgende →</button>
              ) : (
                <button type="button" onClick={save}>Opslaan en afronden</button>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* ── Profiel-modus (terugkerend) ── */
        <div className="profile-sections">
          {SECTIONS.map((section, i) => (
            <SectionCard key={section.key} section={section} defaultOpen={i === 0}>
              {sectionContent[section.key]}
            </SectionCard>
          ))}

          <div className="onboarding-actions">
            <button onClick={save}>Opslaan</button>
            <button className="btn-ghost" onClick={logout}>Afmelden</button>
            {saved && <span className="save-success-inline">✓ Opgeslagen</span>}
          </div>
        </div>
      )}
    </div>
  );
}
